import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';

// Layouts
import { AppLayout } from './components/layout/AppLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { AdminLayout } from './components/layout/AdminLayout';

// Auth pages
import { LoginPage } from './pages/auth/LoginPage';
import { SignUpPage } from './pages/auth/SignUpPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';

// Personal pages
import { DashboardPage } from './pages/personal/DashboardPage';
import { AthletesPage } from './pages/personal/AthletesPage';
import { AthleteDetailPage } from './pages/personal/AthleteDetailPage';
import { WorkoutsPage } from './pages/personal/WorkoutsPage';
import { WorkoutBuilderPage } from './pages/personal/WorkoutBuilderPage';
import { ExercisesPage } from './pages/personal/ExercisesPage';
import { SaaSBillingPage } from './pages/personal/SaaSBillingPage';
import { FinancialPage } from './pages/personal/FinancialPage';
import { WhatsAppPage } from './pages/personal/WhatsAppPage';
import { ContentPage } from './pages/personal/ContentPage';
import { SettingsPage } from './pages/personal/SettingsPage';
import { BioPage } from './pages/personal/BioPage';
import { WearablesPage } from './pages/personal/WearablesPage';
import { PrescribePage } from './pages/personal/PrescribePage';

// Athlete pages
import {
    AthleteDashboardPage,
    AthleteWorkoutPage,
    AthleteProgressPage,
    AthleteCheckInPage,
    AthleteProfilePage,
    AthleteContentPage,
    TrainerSearchPage
} from './pages/athlete';

// Admin pages
import {
    AdminDashboardPage,
    InfluencersPage,
    AffiliateCodesPage,
    CommissionsPage,
    UsersPage,
} from './pages/admin';

// Influencer pages
import { InfluencerDashboardPage } from './pages/influencer';

// Public pages
import { ReferralPage } from './pages/public/ReferralPage';
import { InvitePage } from './pages/public/InvitePage';
import { PublicBioPage } from './pages/public/PublicBioPage';
import { LandingPage } from './pages/public/LandingPage';

// Loading component
function LoadingScreen() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
            <div className="text-center">
                <div className="spinner border-primary-600 w-8 h-8 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
            </div>
        </div>
    );
}

// Protected route wrapper
function ProtectedRoute({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
    const { user, loading, profile } = useAuth();

    if (loading) {
        return <LoadingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (roles && profile && !roles.includes(profile.role)) {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
}

// Role-based redirect component
function RoleRedirect() {
    const { user, profile, loading } = useAuth();

    if (loading || (user && !profile)) return <LoadingScreen />;
    if (!user) return <LandingPage />;

    switch (profile?.role) {
        case 'admin':
            return <Navigate to="/admin" replace />;
        case 'student':
            return <Navigate to="/aluno" replace />;
        case 'personal':
            return <Navigate to="/dashboard" replace />;
        case 'influencer':
            return <Navigate to="/influencer" replace />;
        default:
            return <Navigate to="/login" replace />;
    }
}

export default function App() {
    const { user, loading } = useAuth();

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <Routes>
            {/* Public routes */}
            <Route path="/r/:slug" element={<ReferralPage />} />
            <Route path="/convite/:code" element={<InvitePage />} />
            <Route path="/bio/:slug" element={<PublicBioPage />} />

            {/* Auth routes */}
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            </Route>

            {/* Root dispatcher */}
            <Route path="/" element={<RoleRedirect />} />

            {/* Personal routes */}
            <Route
                element={
                    <ProtectedRoute roles={['personal']}>
                        <AppLayout />
                    </ProtectedRoute>
                }
            >
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/atletas" element={<AthletesPage />} />
                <Route path="/atletas/:id" element={<AthleteDetailPage />} />
                <Route path="/workouts" element={<WorkoutsPage />} />
                <Route path="/workouts/new" element={<WorkoutBuilderPage />} />
                <Route path="/workouts/:id" element={<WorkoutBuilderPage />} />
                <Route path="/exercises" element={<ExercisesPage />} />
                <Route path="/financial" element={<FinancialPage />} />
                <Route path="/billing" element={<SaaSBillingPage />} />
                <Route path="/whatsapp" element={<WhatsAppPage />} />
                <Route path="/content" element={<ContentPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/bio" element={<BioPage />} />
                <Route path="/wearables" element={<WearablesPage />} />
                <Route path="/prescribe/:id" element={<PrescribePage />} />
            </Route>

            {/* Aluno (Student) routes */}
            <Route
                path="/aluno"
                element={
                    <ProtectedRoute roles={['student']}>
                        <AppLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<AthleteDashboardPage />} />
                <Route path="treino/:prescriptionId" element={<AthleteWorkoutPage />} />
                <Route path="progresso" element={<AthleteProgressPage />} />
                <Route path="check-in" element={<AthleteCheckInPage />} />
                <Route path="perfil" element={<AthleteProfilePage />} />
                <Route path="conteudo" element={<AthleteContentPage />} />
                <Route path="buscar-personal" element={<TrainerSearchPage />} />
            </Route>

            {/* Admin routes */}
            <Route
                path="/admin"
                element={
                    <ProtectedRoute roles={['admin']}>
                        <AdminLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<AdminDashboardPage />} />
                <Route path="influencers" element={<InfluencersPage />} />
                <Route path="codes" element={<AffiliateCodesPage />} />
                <Route path="commissions" element={<CommissionsPage />} />
                <Route path="users" element={<UsersPage />} />
            </Route>

            {/* Influencer routes */}
            <Route
                path="/influencer"
                element={
                    <ProtectedRoute roles={['influencer']}>
                        <AppLayout />
                    </ProtectedRoute>
                }
            >
                <Route index element={<InfluencerDashboardPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to={user ? '/' : '/login'} replace />} />
        </Routes>
    );
}
